import React, { useState } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from 'recharts';
import { Holding } from '../../types';
import { CardHeader } from '../ui/Card';
import { useCurrency } from '../../context/CurrencyContext';
import { PieChart as PieIcon } from 'lucide-react';
import { WalletViewLock } from '../Subscription/WalletViewLock';

interface AllocationPieChartProps {
  /**
   * Whole-portfolio holdings, or one wallet's (useWalletFilter). Allocation
   * percentages are always of whatever set is passed, so a wallet's add to 100.
   */
  holdings: Holding[];
  /** The wallet `holdings` belong to, for the header; null for all portfolios. */
  walletLabel?: string | null;
  /** A wallet the user picked but cannot view on their tier: shows a Plus lock. */
  lockedWalletLabel?: string | null;
}

export function AllocationPieChart({ holdings, walletLabel = null, lockedWalletLabel = null }: AllocationPieChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const { fmt } = useCurrency();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload as Holding & { value: number };
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 shadow-xl">
        <p className="text-sm font-bold text-slate-100 mb-1">{d.name}</p>
        <p className="text-xs text-slate-400">{fmt(d.totalValue)}</p>
        <p className="text-xs text-slate-400">{d.allocation.toFixed(1)}% of {walletLabel ?? 'portfolio'}</p>
      </div>
    );
  };

  const title = walletLabel ? `Asset Allocation · ${walletLabel}` : 'Asset Allocation';
  const action = lockedWalletLabel ? <WalletViewLock walletLabel={lockedWalletLabel} /> : undefined;

  if (holdings.length === 0) {
    return (
      <div>
        <CardHeader title={title} subtitle="Portfolio distribution" action={action} />
        <div className="flex flex-col items-center justify-center h-48 text-center">
          <PieIcon className="w-8 h-8 text-slate-700 mb-3" />
          <p className="text-slate-500 text-sm">
            {walletLabel ? `Nothing held in ${walletLabel}` : 'No holdings yet'}
          </p>
        </div>
      </div>
    );
  }

  const sorted = [...holdings].sort((a, b) => b.allocation - a.allocation);
  const data = sorted.map((h) => ({ ...h, value: h.allocation }));

  return (
    <div>
      <CardHeader title={title} subtitle="By coin" action={action} />
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div className="flex-shrink-0 w-48 h-48">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={52}
                outerRadius={76}
                paddingAngle={2}
                dataKey="value"
                onMouseEnter={(_, idx) => setActiveIndex(idx)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {data.map((entry, i) => (
                  <Cell
                    key={entry.coinId}
                    fill={entry.color}
                    opacity={activeIndex === null || activeIndex === i ? 1 : 0.4}
                    stroke="transparent"
                  />
                ))}
              </Pie>
              <Tooltip content={renderTooltip} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex-1 w-full space-y-2.5">
          {data.map((h, i) => (
            <div
              key={h.coinId}
              className="flex items-center gap-3 group cursor-default"
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0 transition-transform group-hover:scale-125"
                style={{ backgroundColor: h.color }}
              />
              <span className="text-sm font-medium text-slate-300 w-12">{h.symbol}</span>
              <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${h.allocation}%`, backgroundColor: h.color }}
                />
              </div>
              <span className="text-sm text-slate-400 w-12 text-right">{h.allocation.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
